import { useEffect, useRef } from "react";
import {
  AiAvatarIcon,
  Button,
  ChatDialog,
  ChatUi,
  CollapsIcon,
  HistoryIcon,
  InputWithLabel,
  SendIcon,
  useKeyTrigger,
  X,
} from "@ptsd/uikit";
import { Circle, ScrollView, Text, View, XStack, YStack } from "tamagui";

import { extractInitials } from "~/utils/extractInitials";
import type { ChatContentProps } from "~/component/feature/chat-bot/chat-holder";
import { useMe } from "~/hooks";

export const ChatContentDesktop = ({
  text,
  chatId,
  onChangeChat,
  onCreateNewChat,
  onChangeText,
  onCreateFirstChat,
  isLoading,
  isChatLoading,
  isDisabled,
  onSubmit,
  messages,
  followups,
  chatHistory,
}: ChatContentProps) => {
  const scrollRef = useRef<ScrollView | null>(null);
  const person = useMe();
  const userName = extractInitials(person.name);

  //TODO:: find better solution for scrolling to bottom
  useEffect(() => {
    try {
      scrollRef.current?.scrollToEnd({ animated: true });
    } catch (e) {
      console.log("scroll to bottom error", e);
    }
  }, [messages, isLoading, followups]);
  useKeyTrigger({ Enter: "Enter" }, () => {
    if (isDisabled || isLoading || text.length === 0) {
      return true;
    }
    onSubmit(text);
  });

  return (
    <>
      <ChatUi.Container
        height={500}
        width={380}
        style={{
          backgroundColor: "white",
          borderWidth: 1,
          borderColor: "#CDD3DC",
          borderRadius: 14,
          overflow: "hidden",
          zIndex: 100,
        }}
      >
        <ChatUi.SidebarProvider width={320}>
          <ChatUi.SidebarContainer
            style={{
              flexDirection: "row",
              height: "100%",
              width: "100%",
            }}
          >
            <ChatUi.SidebarContent
              style={{
                borderRightWidth: 1,
                borderColor: "#CDD3DC",
                justifyContent: "space-between",
              }}
            >
              <View
                height={80}
                borderColor={"$gray-200"}
                borderBottomWidth={"$0.1"}
                alignItems="flex-start"
                gap={"$2"}
                padding={"$2"}
              >
                <XStack
                  alignItems="center"
                  width={"100%"}
                  justifyContent="space-between"
                >
                  <Text
                    fontFamily={"$body"}
                    fontWeight={"$4"}
                    fontSize={"$3"}
                    lineHeight={"$3"}
                    color={"$gray-primary-900"}
                  >
                    Історія діалогів
                  </Text>

                  <Button
                    variant="primary"
                    size="md"
                    rounded="lg"
                    onPress={onCreateNewChat}
                  >
                    Новий чат
                  </Button>
                </XStack>
                {/*<SearchInput placeholder="Пошук діалогів..." /> */}
              </View>
              <ScrollView
                height={"100%"}
                paddingVertical={"$3"}
                paddingHorizontal={"$2"}
                contentContainerStyle={{
                  gap: "$2",
                }}
              >
                {chatHistory?.map((item) => (
                  <ChatUi.HistoryItem
                    onPress={() => onChangeChat(item.id)}
                    isActive={item.id === chatId}
                    key={item.id}
                    id={item.id}
                    summaryId={item.summary_id}
                    name={item.name}
                    createdAt={item.created_at}
                    tags={item.tags}
                    chatSummary={item.chat_summary}
                  />
                ))}
              </ScrollView>

              <View
                height={106}
                borderColor={"$gray-200"}
                borderTopWidth={"$0.1"}
                justifyContent="center"
                paddingHorizontal={"$2"}
                width="100%"
              >
                <XStack
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text
                    fontFamily={"$body"}
                    fontWeight={"$4"}
                    fontSize={"$3"}
                    lineHeight={"$3"}
                    color={"$gray-secondary-400"}
                  >
                    Всього діалогів
                  </Text>
                  <Circle size={"$4"} backgroundColor={"$lightblue-700-010"}>
                    <Text
                      fontFamily={"$body"}
                      fontWeight={"$4"}
                      fontSize={"$3"}
                      lineHeight={"$3"}
                      color={"$lightblue-700"}
                    >
                      {chatHistory?.length ?? 0}
                    </Text>
                  </Circle>
                </XStack>
              </View>
            </ChatUi.SidebarContent>
            <View
              style={{
                flex: 1,
                justifyContent: "space-between",
              }}
            >
              <ChatUi.Header
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                paddingHorizontal={"$3"}
              >
                {(expanded) => (
                  <>
                    <XStack gap={"$1"}>
                      {expanded && (
                        <ChatUi.SidebarToggle asChild>
                          <Button
                            variant="outlined"
                            size="md"
                            rounded="lg"
                            mode="icon"
                          >
                            <HistoryIcon />
                          </Button>
                        </ChatUi.SidebarToggle>
                      )}
                      <YStack>
                        <XStack alignItems="center" gap={"$1"}>
                          <Text
                            fontFamily={"$body"}
                            fontWeight={"$5"}
                            fontSize={"$3"}
                            lineHeight={"$3"}
                            color={"white"}
                          >
                            AI-помічник
                          </Text>
                          <AiAvatarIcon />
                        </XStack>
                        <Text
                          fontFamily={"$body"}
                          fontWeight={"$4"}
                          fontSize={"$3"}
                          lineHeight={"$3"}
                          color={"$gray-secondary-400"}
                          ellipsizeMode="tail"
                          numberOfLines={1}
                          width={expanded ? "calc(100vw - 150px)" : "250px"}
                        >
                          {chatHistory?.find((chat) => chat.id === chatId)
                            ?.name ?? "Без назви"}
                        </Text>
                      </YStack>
                    </XStack>
                    <XStack>
                      <ChatUi.Toggle size="sm" mode="icon" padding={0}>
                        <CollapsIcon />
                      </ChatUi.Toggle>
                      <ChatDialog.Close size="sm" mode="icon" padding={0}>
                        <X color="#CDD3DC" />
                      </ChatDialog.Close>
                    </XStack>
                  </>
                )}
              </ChatUi.Header>
              <View flex={1}>
                <View
                  style={{
                    flex: 1,
                    maxWidth: 758,
                    width: "100%",
                    alignSelf: "center",
                  }}
                >
                  <ScrollView
                    ref={scrollRef}
                    contentContainerStyle={{
                      width: "100%",
                      alignSelf: "center",
                      gap: 20,
                      justifyContent: "flex-end",
                      paddingBottom: 20,
                      paddingTop: "$3",
                      paddingHorizontal: "$3",
                    }}
                  >
                    {isChatLoading && <ChatUi.Loader />}
                    {messages?.map(({ mine, at, isIntro, ...rest }, i) => (
                      <ChatUi.Message
                        key={i}
                        userInitial={userName}
                        mine={mine}
                        atText={
                          at?.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          }) ?? ""
                        }
                        {...rest}
                      >
                        {isIntro && (
                          <View display={"inline-flex"}>
                            <Button
                              rounded="md"
                              size="md"
                              variant="primary"
                              onPress={onCreateFirstChat}
                            >
                              Що показали результати?
                            </Button>
                          </View>
                        )}
                      </ChatUi.Message>
                    ))}
                    {!isLoading && followups && followups.length > 0 ? (
                      <ChatUi.QuickAnswers
                        shortAnswers={followups}
                        onPress={(question) => onSubmit(question)}
                      />
                    ) : null}
                    {isLoading && (
                      <ChatUi.TypingDots
                        marginLeft={"$6"}
                        paddingBottom={"$3"}
                      />
                    )}
                  </ScrollView>
                </View>
              </View>
              <ChatUi.Footer
                paddingHorizontal={"$3"}
                alignItems="center"
                justifyContent="center"
                flexDirection="row"
                gap={"$1"}
              >
                {(expanded) => (
                  <>
                    <InputWithLabel
                      variant="stacked"
                      borderColor={"$gray-100"}
                      height={"$5"}
                      maxWidth={682}
                      placeholder="Напишіть повідомлення..."
                      value={text}
                      onChangeText={onChangeText}
                      {...(expanded && {
                        height: "$8.5",
                      })}
                    />

                    <Button
                      mode="icon"
                      variant="gray"
                      rounded="lg"
                      height={"$5"}
                      width={"$5"}
                      backgroundColor={"$gray-secondary-400"}
                      onPress={() => onSubmit(text)}
                      disabled={isDisabled || isLoading}
                      borderWidth={0}
                      {...(expanded && {
                        height: "$8.5",
                        width: "$8.5",
                      })}
                    >
                      <SendIcon
                        height={20}
                        width={20}
                        {...(expanded && {
                          height: 36,
                          width: 36,
                        })}
                      />
                    </Button>
                  </>
                )}
              </ChatUi.Footer>
            </View>
          </ChatUi.SidebarContainer>
        </ChatUi.SidebarProvider>
      </ChatUi.Container>
    </>
  );
};
